import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Skeleton } from '../ui/Skeleton';

interface AnalyticsData {
  period: string;
  totals: {
    views: number;
    starts: number;
    completions: number;
    dropOffs: number;
    avgCompletionTime: number;
    conversionRate: number;
    dropOffRate: number;
  };
  daily: Array<{
    date: string;
    views: number;
    starts: number;
    completions: number;
    avg_completion_time_seconds: number;
    drop_offs: number;
  }>;
  fieldAnalytics: Array<{
    field_name: string;
    interaction_count: number;
    avg_time_ms: number;
    error_count: number;
  }>;
  stepDropOffs: Array<{
    current_step: number;
    abandoned_count: number;
  }>;
  deviceBreakdown: Array<{
    device_type: string;
    count: number;
  }>;
}

interface FormAnalyticsProps {
  formId: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function FormAnalytics({ formId, isOpen, onClose }: FormAnalyticsProps) {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    if (isOpen && formId) {
      loadAnalytics();
    }
  }, [isOpen, formId, period]);

  async function loadAnalytics() {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/forms/${formId}/analytics?period=${period}`);
      if (!response.ok) throw new Error('Failed to load analytics');
      const result = await response.json() as AnalyticsData;
      setData(result);
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }

  function formatTime(seconds: number): string {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Form Analytics</h2>
            <p className="text-sm text-gray-500">Track performance and user behavior</p>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as '7d' | '30d' | '90d')}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="space-y-8">
              {/* Key Metrics Skeleton */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <Skeleton className="w-10 h-10 rounded-lg" />
                      <div className="flex-1">
                        <Skeleton className="h-4 w-20 mb-1" />
                        <Skeleton className="h-6 w-16" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Funnel Skeleton */}
              <div className="bg-gray-50 rounded-lg p-6">
                <Skeleton className="h-4 w-36 mb-4" />
                <div className="flex items-center gap-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex-1 text-center">
                      <Skeleton className="h-16 w-full rounded mb-2" />
                      <Skeleton className="h-6 w-12 mx-auto mb-1" />
                      <Skeleton className="h-3 w-16 mx-auto" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Chart Skeleton */}
              <div>
                <Skeleton className="h-4 w-24 mb-4" />
                <div className="h-48 flex items-end gap-1">
                  {Array.from({ length: 15 }).map((_, i) => (
                    <Skeleton
                      key={i}
                      className="flex-1 rounded-t"
                      style={{ height: `${Math.random() * 60 + 20}%` }}
                    />
                  ))}
                </div>
              </div>

              {/* Table Skeleton */}
              <div>
                <Skeleton className="h-4 w-32 mb-4" />
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-12" />
                      <Skeleton className="h-4 w-8" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
              <button
                onClick={loadAnalytics}
                className="mt-4 text-primary-600 hover:text-primary-700"
              >
                Try again
              </button>
            </div>
          ) : data ? (
            <div className="space-y-8">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard
                  label="Total Views"
                  value={data.totals.views}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  }
                />
                <MetricCard
                  label="Submissions"
                  value={data.totals.completions}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                  color="green"
                />
                <MetricCard
                  label="Conversion Rate"
                  value={`${data.totals.conversionRate}%`}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  }
                  color="blue"
                />
                <MetricCard
                  label="Avg. Completion Time"
                  value={formatTime(data.totals.avgCompletionTime)}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                  color="purple"
                />
              </div>

              {/* Funnel */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Conversion Funnel</h3>
                <div className="flex items-center gap-4">
                  <FunnelStep label="Views" value={data.totals.views} width={100} color="gray" />
                  <FunnelArrow />
                  <FunnelStep
                    label="Started"
                    value={data.totals.starts}
                    width={data.totals.views > 0 ? (data.totals.starts / data.totals.views) * 100 : 0}
                    color="blue"
                  />
                  <FunnelArrow />
                  <FunnelStep
                    label="Completed"
                    value={data.totals.completions}
                    width={data.totals.views > 0 ? (data.totals.completions / data.totals.views) * 100 : 0}
                    color="green"
                  />
                </div>
              </div>

              {/* Daily Chart */}
              {data.daily.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Daily Activity</h3>
                  <div className="h-48 flex items-end gap-1">
                    {data.daily.map((day) => {
                      const maxValue = Math.max(...data.daily.map(d => d.completions + d.drop_offs));
                      const height = maxValue > 0 ? ((day.completions + day.drop_offs) / maxValue) * 100 : 0;
                      const completionHeight = maxValue > 0 ? (day.completions / maxValue) * 100 : 0;

                      return (
                        <div key={day.date} className="flex-1 flex flex-col items-center group relative">
                          <div className="w-full flex flex-col-reverse h-40">
                            <div
                              className="w-full bg-green-500 rounded-t"
                              style={{ height: `${completionHeight}%` }}
                            />
                            <div
                              className="w-full bg-red-300"
                              style={{ height: `${height - completionHeight}%` }}
                            />
                          </div>
                          {/* Tooltip */}
                          <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                            {new Date(day.date).toLocaleDateString()}<br />
                            Completed: {day.completions}<br />
                            Drop-offs: {day.drop_offs}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>{data.daily[0]?.date}</span>
                    <div className="flex gap-4">
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 bg-green-500 rounded" /> Completed
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 bg-red-300 rounded" /> Drop-offs
                      </span>
                    </div>
                    <span>{data.daily[data.daily.length - 1]?.date}</span>
                  </div>
                </div>
              )}

              {/* Field Analytics */}
              {data.fieldAnalytics.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Field Performance</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="pb-2 font-medium">Field</th>
                          <th className="pb-2 font-medium text-right">Interactions</th>
                          <th className="pb-2 font-medium text-right">Avg. Time</th>
                          <th className="pb-2 font-medium text-right">Errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.fieldAnalytics.slice(0, 10).map((field) => (
                          <tr key={field.field_name} className="border-b border-gray-100">
                            <td className="py-2">{field.field_name}</td>
                            <td className="py-2 text-right">{field.interaction_count}</td>
                            <td className="py-2 text-right">{Math.round(field.avg_time_ms / 1000)}s</td>
                            <td className="py-2 text-right">
                              {field.error_count > 0 && (
                                <span className="text-red-600">{field.error_count}</span>
                              )}
                              {field.error_count === 0 && '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Device Breakdown */}
              {data.deviceBreakdown.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Device Breakdown</h3>
                  <div className="flex gap-6">
                    {data.deviceBreakdown.map((device) => {
                      const total = data.deviceBreakdown.reduce((sum, d) => sum + d.count, 0);
                      const percentage = total > 0 ? Math.round((device.count / total) * 100) : 0;

                      return (
                        <div key={device.device_type} className="flex items-center gap-2">
                          <span className="text-2xl">
                            {device.device_type === 'mobile' ? '📱' : device.device_type === 'tablet' ? '📲' : '💻'}
                          </span>
                          <div>
                            <p className="font-medium capitalize">{device.device_type}</p>
                            <p className="text-sm text-gray-500">{percentage}% ({device.count})</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No Data Message */}
              {data.totals.views === 0 && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="text-gray-500">No analytics data yet</p>
                  <p className="text-sm text-gray-400 mt-1">Data will appear once users start viewing your form</p>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// Metric Card Component
interface MetricCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'gray' | 'green' | 'blue' | 'purple';
}

function MetricCard({ label, value, icon, color = 'gray' }: MetricCardProps) {
  const colorClasses = {
    gray: 'bg-gray-100 text-gray-600',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-xl font-semibold">{value}</p>
        </div>
      </div>
    </div>
  );
}

// Funnel Step Component
interface FunnelStepProps {
  label: string;
  value: number;
  width: number;
  color: 'gray' | 'blue' | 'green';
}

function FunnelStep({ label, value, width, color }: FunnelStepProps) {
  const colorClasses = {
    gray: 'bg-gray-300',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
  };

  return (
    <div className="flex-1 text-center">
      <div className="h-16 flex items-end justify-center mb-2">
        <div
          className={`${colorClasses[color]} rounded-t transition-all`}
          style={{ width: `${Math.max(width, 10)}%`, height: `${Math.max(width, 20)}%`, minHeight: '24px' }}
        />
      </div>
      <p className="text-lg font-semibold">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}

function FunnelArrow() {
  return (
    <svg className="w-6 h-6 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}
